import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

/** Відповідність медіазапиту (підписка без зайвих рендерів). */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (cb: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener('change', cb);
      return () => mql.removeEventListener('change', cb);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}

export const useIsDesktop = () => useMediaQuery('(min-width: 64rem)');
export const useIsPhone = () => !useMediaQuery('(min-width: 40rem)');
export const useReducedMotion = () => useMediaQuery('(prefers-reduced-motion: reduce)');

/** Стан мережі браузера. */
export function useOnline(): boolean {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener('online', cb);
      window.addEventListener('offline', cb);
      return () => {
        window.removeEventListener('online', cb);
        window.removeEventListener('offline', cb);
      };
    },
    () => navigator.onLine,
    () => true,
  );
}

export function useDebounced<T>(value: T, delay = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return v;
}

/** Значення в localStorage (зручності окремого браузера; збій сховища не ламає інтерфейс). */
export function useLocalStorage<T>(key: string, initial: T): [T, (v: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => readStorage(key, initial));
  const set = useCallback(
    (v: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const next = typeof v === 'function' ? (v as (p: T) => T)(prev) : v;
        writeStorage(key, next);
        return next;
      });
    },
    [key],
  );
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === key) setValue(readStorage(key, initial));
    };
    // Зміни з інших екземплярів хука в цій самій вкладці (напр. «Останні» проєкти в бічній панелі).
    const onLocal = (e: Event) => {
      if ((e as CustomEvent<string>).detail === key) setValue(readStorage(key, initial));
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('ers-storage', onLocal);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('ers-storage', onLocal);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return [value, set];
}

export function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw == null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function writeStorage<T>(key: string, value: T) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* сховище недоступне — ігноруємо */
  }
  window.setTimeout(() => window.dispatchEvent(new CustomEvent('ers-storage', { detail: key })), 0);
}

/** Чи зараз фокус у полі введення (щоб гарячі клавіші не заважали набору). */
export function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'submit', 'reset'].includes(type);
  }
  return false;
}

/** Останнє значення без перезапуску ефектів. */
export function useLatest<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

/** Копіювання в буфер обміну з позначкою «скопійовано» на 2 с. */
export function useCopy(): [boolean, (text: string) => Promise<boolean>] {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      if (!ok) return false;
    }
    setCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 2000);
    return true;
  }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return [copied, copy];
}
